import React, { useCallback, useContext, useMemo } from "react";
import { connect } from "react-redux";
import { formatDistanceToNowStrict, isValid } from "date-fns";
import { useParams } from "react-router-dom";

import { useMapPathZoom } from "./use-map-zoom";
import SidepanelHeader from "./ui/sidepanel-header";
import { calculateCrewStatus, CrewIcon, CrewPin } from "./ui/crew";
import {
  CompleteCheck,
  RouteDetailsProgressBar,
} from "./RouteDetails/Progress";
import { useQueryClient } from "@tanstack/react-query";
import LoadingIcon from "../../Loaders/LoadingIcon";
import { StartEndMapMarker } from "../../../pages/Admin/Routes/RoutePage/Map/Marker";
import { resolveRouteColorPalette } from "./routeColor";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { ActiveRouteDataContext } from "./ActiveRouteDataLayer";
import { AxiosContext } from "../../../services/axios/AxiosContext";
import useNavigateKeepSearchParams from "../../../hooks/useNavigateKeepSearchParams";

const ActiveRoutesRouteDetail = ({ currentUser }) => {
  // use axios from a context so that our parent can override our axios instance.
  const axios = useContext(AxiosContext);
  const { activeRoutesQuery, activeRoutes } = useContext(
    ActiveRouteDataContext,
  );
  const map = useMap();
  let baseUrl = window.location.pathname.match(/(.*active-routes)\//)[1];

  const queryClient = useQueryClient();

  let { routeId, propertyId } = useParams();

  let activePropertyId = propertyId;
  let navigate = useNavigateKeepSearchParams(["app_version"]);

  const loading = activeRoutesQuery.isLoading && !activeRoutes.length;
  let data = activeRoutes.find((route) => route._id == routeId);
  const paused = data?.status === "paused";

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (paused) return;

    // Display an alert asking if the user is sure they want to delete the route
    if (
      window.confirm(
        "Are you sure you want to discontinue this route? The active route will be discontinued, but no data will be lost.",
      )
    ) {
      await axios.delete(`activeRoute/operator/${data.operator.uid}/delete`);
      queryClient.invalidateQueries({
        queryKey: ["company", currentUser.currentCompanyId, "activeRoutes-new"],
      });
      navigate(`${baseUrl}/main`);
    }
  };

  const handleCrewClick = (e) => {
    e.preventDefault();

    // Navigate to crew detail
    navigate(`${baseUrl}/main/crew/${data.operator._id}`);

    // Center map on crew location if available
    if (data.operator.lastKnownLocation) {
      map.panTo({
        lat: data.operator.lastKnownLocation.lat,
        lng: data.operator.lastKnownLocation.lng,
      });
    }
  };

  // Memoize resetPaths to avoid creating a new array on every render
  // This prevents the useMapPathZoom effect from running unnecessarily.
  // Exclude paused routes so their paths don't affect zoom reset.
  const resetPaths = useMemo(() => {
    return activeRoutes
      .filter((activeRoute) => activeRoute.status !== "paused")
      .map((activeRoute) => activeRoute.route?.drivingPath)
      .filter(Boolean);
  }, [activeRoutes]);

  // Memoize the callback to avoid creating a new function on every render
  const extendBoundsCallback = useCallback(
    (bounds) => {
      if (!paused && data?.operator?.lastKnownLocation) {
        bounds.extend({
          lat: data.operator.lastKnownLocation.lat,
          lng: data.operator.lastKnownLocation.lng,
        });
      }
    },
    [paused, data?.operator?.lastKnownLocation],
  );

  useMapPathZoom(
    data?.route?.drivingPath ? [data.route.drivingPath] : [],
    resetPaths,
    extendBoundsCallback,
  );

  if (loading) return <LoadingIcon />;

  if (data === undefined) {
    navigate(`${baseUrl}/main`);
    return <p>This route has been ended. Going back to the main map</p>;
  }

  const clickProperty = (newPropertyId) => {
    if (newPropertyId !== activePropertyId) {
      navigate(`${baseUrl}/main/${data._id}/${newPropertyId}`);
    } else {
      navigate(`${baseUrl}/main/${data._id}`);
    }
  };

  const routeColor = data.route.color ?? "emerald";
  const pausedDate = data.pausedAt ? new Date(data.pausedAt) : null;

  const [crewStatus, statusDuration] = calculateCrewStatus(
    data.operator?.lastKnownLocation?.createdAt,
  );
  let foreman = data.operator?.firstName + " " + data.operator?.lastName;

  let statusUpdated = "";
  if (statusDuration?.hours > 0) {
    statusUpdated = "over an hour";
  } else if (statusDuration?.minutes > 1) {
    statusUpdated = `${statusDuration.minutes} minutes`;
  } else if (statusDuration !== null) {
    statusUpdated = "about a minute";
  }

  return (
    <>
      <div className="h-full grid grid-rows-[60px_auto_77px]">
        <div className="px-5 pb-4 border-b border-gray-200">
          <div className="flex items-end justify-between">
            <SidepanelHeader
              onClose={(e) => {
                e.preventDefault();
                navigate(`${baseUrl}/main`);
              }}
            >
              {data.route.name}
            </SidepanelHeader>
          </div>
        </div>
        <div className="px-4 py-5 md-py-16">
          <button
            onClick={handleCrewClick}
            className="flex items-center gap-x-3 pt-2 text-base hover:cursor-pointer hover:underline"
          >
            <CrewIcon
              color={routeColor}
              lessSpace={true}
              enabled={crewStatus === "active"}
              initials={data.operator.firstName[0] + data.operator.lastName[0]}
            />
            <span className="flex-auto">{foreman}</span>
          </button>
          {paused ? (
            <div className="mt-3 flex items-center gap-2">
              <span className="rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                Paused
              </span>
              <span className="text-xs text-gray-500">
                {pausedDate && isValid(pausedDate)
                  ? `Paused ${formatDistanceToNowStrict(pausedDate, { addSuffix: true })}`
                  : "Pause time unavailable"}
              </span>
            </div>
          ) : null}
          <RouteDetailsProgressBar
            jobs={data.route.jobs}
            color={routeColor}
            onClick={(propertyId) => clickProperty(propertyId)}
          />
        </div>

        <div className="grid grid-cols-6 gap-2 border-t border-gray-200 px-4 py-5 sm:px-6">
          {!paused ? (
            <div className="col-span-2">
              <button
                type="submit"
                onClick={handleDelete}
                className="inline-flex justify-center rounded-md bg-snow-red px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Discontinue
              </button>
            </div>
          ) : null}
          {!paused && statusUpdated ? (
            <div className="col-span-4 justify-self-end place-content-end">
              <span className="text-xs text-gray-600 italic">
                Updated {statusUpdated} ago
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Mapping items */}
      {!paused && data.operator.lastKnownLocation ? (
        <CrewPin
          position={data.operator.lastKnownLocation}
          status={crewStatus}
          color={routeColor}
          initials={data.operator.firstName[0] + data.operator.lastName[0]}
        />
      ) : null}
      {data.route.start?.location ? (
        <StartEndMapMarker type="start" position={data.route.start.location} />
      ) : null}
      {data.route.end?.location ? (
        <StartEndMapMarker type="end" position={data.route.end.location} />
      ) : null}
      {/* TODO: if jobs loop, we may have problems by using property.id as a unique key */}
      {routeColor
        ? data.route.jobs.map((property) => {
            return property.location?.lat && property.location?.lng ? (
              <>
                <MapMarkerWithStatus
                  key={property._id}
                  color={routeColor}
                  status={property.status}
                  isActive={property._id === activePropertyId}
                  onClick={() => {
                    clickProperty(property._id);
                  }}
                  position={property.location}
                />
              </>
            ) : null;
          })
        : null}
      {/* <Outlet /> */}
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(ActiveRoutesRouteDetail);

const MapMarkerWithStatus = ({
  key,
  color,
  status,
  isActive,
  onClick,
  position,
}) => {
  const highlight = isActive || status === "current";
  const isComplete = status === "complete";
  const higherZIndex = isActive || status === "complete"; // per discussions w Daniel

  const bgColor = highlight
    ? resolveRouteColorPalette(color).bgHover
    : isComplete
      ? `${resolveRouteColorPalette(color).bg} is-new`
      : resolveRouteColorPalette(color).bg;

  const highlighted = highlight ? "highlighted" : "";

  return (
    <AdvancedMarker
      key={key}
      zIndex={higherZIndex ? 20 : 10}
      position={position}
      onClick={onClick}
    >
      <div className={`map-marker-custom-pin ${bgColor} ${highlighted}`}>
        <div
          className={`map-marker-inner-circle ${bgColor} flex justify-center items-center`}
        >
          <div style={{ marginTop: "-3px" }}>
            {isComplete ? (
              <div
                className="size-4 flex justify-center items-center"
                style={{
                  width: "1.25rem",
                }}
              >
                <CompleteCheck />
              </div>
            ) : (
              <div className={`size-2 rounded-full bg-white`}></div>
            )}
          </div>
        </div>
      </div>
    </AdvancedMarker>
  );
};
